import {
  AudioCurrentlyPlaying,
  AudioTrack,
  AudioTrackInfoRecord,
  SpotifyPlaylist,
  URLArray,
} from "src/types";
import { urlSource } from "../utils/utils";
import { getDatabase, ref, onValue } from "firebase/database";
import { Button, CircularProgress } from "@mui/material";
import { useEffect, useState } from "preact/hooks";
import { msToMMSS } from "../utils/datetime";

import "./playlists.scss";
import FullscreenButton from "./fullscreen_button";

export const renderPlaylistEmbeds = (
  playlists: URLArray,
  with_embeds = false
) =>
  playlists.map((playlist) =>
    renderPlaylistEmbed(playlist.name || "Playlist", playlist.url, with_embeds)
  );

export const renderPlaylistEmbed = (
  name: string,
  url: string,
  with_embeds = false
) => {
  const decoded = decodeURIComponent(url);
  const source = urlSource(decoded);
  const _embed = () => {
    const newSrc = decoded.replace(
      "spotify.com/playlist",
      "spotify.com/embed/playlist"
    );
    return (
      <iframe
        style="border-radius:12px; margin-top:0.25em;"
        src={newSrc}
        width="100%"
        height="152"
        frameBorder="0"
        allowFullScreen={true}
        allow="encrypted-media"
        loading="lazy"
      ></iframe>
    );
  };
  return (
    <>
      <div>
        <a href={decoded}>{name}</a>{" "}
        {source && !with_embeds && " (" + source + " link)"}
      </div>
      {with_embeds && _embed()}
    </>
  );
};

interface PlaylistDisplayProps {
  currentlyPlaying: AudioCurrentlyPlaying;
  playlist?: SpotifyPlaylist;
  reloadCallback?: () => void;
  showBefore?: number;
  trackInfo: AudioTrackInfoRecord;
  editCallback?: (trackId: string) => void;
}

export const PlaylistDisplay = (props: PlaylistDisplayProps) => {
  const isPlaying = props.currentlyPlaying.is_playing;
  const showBefore = props.showBefore ?? 3;
  const showAfter = 20;

  const [time, setTime] = useState<number>();

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(Date.now());
    }, 100);

    return () => clearInterval(timer);
  }, [props]);

  const tracks = props.playlist?.tracks || [];
  const shuffling =
    props.currentlyPlaying.shuffle_state ||
    props.currentlyPlaying.smart_shuffle;

  const [offset, currentTrackProgress] = (() => {
    let offset = tracks.findIndex(
      (track) => track.id === props.currentlyPlaying?.id
    );
    let currentTrackProgress =
      (time || Date.now()) - props.currentlyPlaying.started_time;
    if (offset < 0 || shuffling || !isPlaying) {
      return [offset, currentTrackProgress];
    }

    while (
      offset < tracks.length - 1 &&
      currentTrackProgress > tracks[offset].duration
    ) {
      currentTrackProgress -= tracks[offset].duration;
      offset++;
    }
    return [offset, currentTrackProgress];
  })();

  // if more than half of the tracks have a tempo, then show it
  const showBPM =
    tracks
      .map((track) => props.trackInfo[track.id]?.tempo)
      .filter((tempo) => tempo && tempo > 0).length >
    tracks.length / 2;

  const currentTrack = offset > 0 ? tracks[offset] : props.currentlyPlaying;

  const currentTrackPlayed =
    isPlaying && Math.min(currentTrack.duration, currentTrackProgress);

  const tableHeader = (
    <thead>
      <tr>
        <th>#</th>
        <th>Title</th>
        <th>Artist</th>
        {showBPM && <th>BPM</th>}
        <th class="duration">Duration</th>
        {props.editCallback && <th class="hide-fullscreen"></th>}
      </tr>
    </thead>
  );

  const renderTrack = (
    track: AudioTrack,
    className: "current" | "previous" | "",
    number: number
  ) => {
    const current = !!(className === "current" && currentTrackPlayed);

    const duration = () => {
      if (current) {
        return (
          <>
            {msToMMSS(currentTrackPlayed)} / {msToMMSS(track.duration)}
          </>
        );
      }
      return msToMMSS(track.duration);
    };

    const style = current
      ? {
          "--progress": 100 * (currentTrackPlayed / track.duration) + "%",
        }
      : undefined;

    const tempo = props.trackInfo[track.id]?.tempo;

    const artists = track.artists.split(/,\s*/);

    return (
      <tr class={className} style={style}>
        <td>{number + 1}</td>
        <td>{track.name}</td>
        <td class="artists">
          {artists.map((artist, i) => (
            <>
              <span>{artist}</span>
              {i < artists.length - 1 && <wbr />}
            </>
          ))}
        </td>
        {showBPM && <td>{tempo && Math.round(tempo)}</td>}
        <td class="duration">{duration()}</td>
        {props.editCallback && (
          <td class="hide-fullscreen">
            <Button onClick={() => props.editCallback!(track.id)}>Edit</Button>
          </td>
        )}
      </tr>
    );
  };

  const sumDurations = (tracks: AudioTrack[]) =>
    tracks
      .map((track) => track.duration)
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0);

  if (!props.playlist || offset < 0) {
    return (
      <div class="playlist">
        <table>
          {tableHeader}
          <tbody>{renderTrack(currentTrack, "current", 0)}</tbody>
        </table>
      </div>
    );
  }

  const start = Math.max(0, offset - showBefore);
  const end = Math.min(tracks.length, offset + showAfter + 1);
  const previous = tracks.slice(0, offset);
  const previousShow = tracks.slice(start, offset);

  const timePlayed = sumDurations(previous) + currentTrackProgress;
  const timeTotal = sumDurations(tracks);
  const timeRemaining = Math.max(0, timeTotal - timePlayed);
  const remainingCount = tracks.length - offset;

  const refreshLength =
    props.currentlyPlaying.nextUpdate - props.currentlyPlaying.lastUpdate;
  const timeSinceRefresh = Date.now() - props.currentlyPlaying.lastUpdate;
  const refreshProgress = (100 * timeSinceRefresh) / refreshLength;

  const playingState = () => {
    if (shuffling || !isPlaying) {
      return <></>;
    }
    const trackLabel = "song" + (remainingCount === 1 ? "" : "s");
    return (
      <div>
        Remaining: {remainingCount} {trackLabel}, {msToMMSS(timeRemaining)}
      </div>
    );
  };

  const progressColour =
    !isPlaying || refreshProgress < 107
      ? "info"
      : refreshProgress < 125
        ? "warning"
        : "error";

  const playlistProgressStyle =
    timeTotal && isPlaying && !shuffling
      ? { "--playlist-progress": (100 * timePlayed) / timeTotal + "%" }
      : {};

  const playingVerb = isPlaying ? "Playing" : "Stopped";
  const shuffleLabel = shuffling ? <small> (shuffle)</small> : "";

  return (
    <div class="playlist">
      <div class="heading">
        {props.reloadCallback && (
          <div class="refresh-timer">
            <CircularProgress
              variant="determinate"
              value={Math.min(100, refreshProgress)}
              size={50}
              onClick={props.reloadCallback}
              color={progressColour}
              thickness={5}
            />
          </div>
        )}
        {props.playlist.name && (
          <h2 class="playlist-progress" style={playlistProgressStyle}>
            {playingVerb}
            {shuffleLabel}: {props.playlist.name}
          </h2>
        )}
        {playingState()}
      </div>
      <hr />
      <table>
        {tableHeader}
        <tbody>
          {previousShow.map((track, i) =>
            renderTrack(track, shuffling ? "" : "previous", start + i)
          )}
          {renderTrack(tracks[offset], "current", offset)}
          {tracks
            .slice(offset + 1, end)
            .map((track, i) => renderTrack(track, "", offset + i + 1))}
        </tbody>
      </table>
    </div>
  );
};

interface MusicDataRTDB {
  from?: string;
  lastChanged: number;
  online: Record<string, boolean>;
  currentlyPlaying?: AudioCurrentlyPlaying;
  trackInfo?: AudioTrackInfoRecord;
  playlist?: SpotifyPlaylist;
}
export const PlaylistDisplayFromRTDB = () => {
  const [musicData, setMusicData] = useState<MusicDataRTDB>();

  useEffect(() => {
    const starCountRef = ref(getDatabase(), "music");
    onValue(starCountRef, (snapshot) => {
      setMusicData(snapshot.val());
    });
  }, []);

  const since = new Date();
  since.setMinutes(since.getMinutes() - 10);

  if (
    !musicData?.currentlyPlaying?.is_playing ||
    musicData.lastChanged < since.getTime() ||
    !Object.entries(musicData.online || {}).length
  ) {
    return <></>;
  }

  const props: PlaylistDisplayProps = {
    ...musicData,
    currentlyPlaying: musicData.currentlyPlaying as AudioCurrentlyPlaying,
    trackInfo: musicData.trackInfo || {},
  };

  return (
    <div id="playlistFullscreen">
      <FullscreenButton id="playlistFullscreen" />
      <PlaylistDisplay {...props} />
    </div>
  );
};
